import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import BoxedItem from '../../../../../../../components/global/BoxedItem';
import ImageUpload from '../../../../../../../components/global/ImageUpload';
import useAssetImageUpoad from '../../../../../../../hooks/useAssetImageUpload';
import Button from '../../../../../../../components/global/Button';
import CardTemplateSettings from '../CardTemplateSettings';

const propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  cardSideGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    side: PropTypes.string.isRequired,
    card_templates: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        asset_image_id: PropTypes.string,
      }).isRequired,
    ).isRequired,
    allowed_card_templates: PropTypes.number,
  }).isRequired,
  cardTemplates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  getElementConfigurationStateById: PropTypes.func.isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
};

const CardSideGroupSettings = ({
  className,
  onChange,
  cardSideGroup,
  cardTemplates,
  getElementConfigurationStateById,
  onElementConfigurationChange,
}) => {
  const classes = classNames({
    'card-side-group-settings mb-3': true,
    [className]: !!className,
  });

  const { uploading, onUpload, onUploadReset, image } = useAssetImageUpoad({
    onImageUploaded: assetImage => {
      onChange({
        asset_image_id: assetImage.id,
      });
    },
  });

  const onCardTemplateToggle = cardTemplate => {
    const selectedCardTemplateIds = cardSideGroup.card_templates.map(
      ({ id }) => id,
    );

    if (selectedCardTemplateIds.includes(cardTemplate.id)) {
      onChange({
        card_templates: cardSideGroup.card_templates.filter(
          ({ id }) => id !== cardTemplate.id,
        ),
      });

      return;
    }

    onChange({
      card_templates: [
        ...cardSideGroup.card_templates,
        { id: cardTemplate.id },
      ],
    });
  };

  const selectedCardTemplates = cardTemplates.filter(cardTemplate => {
    return cardSideGroup.card_templates
      .map(({ id }) => id)
      .includes(cardTemplate.id);
  });

  const onCardTemplateChange = value => {
    onChange({
      card_templates: cardSideGroup.card_templates.map(cardTemplate => {
        if (cardTemplate.id === value.id) {
          return {
            ...cardTemplate,
            ...value,
          };
        }

        return cardTemplate;
      }),
    });
  };

  return (
    <BoxedItem className={classes}>
      <h5>{cardSideGroup.name}</h5>

      <h6>Preview Image:</h6>
      <ImageUpload
        id={`${cardSideGroup.name}-image-upload`}
        loading={uploading}
        onImageDismiss={() => {
          onUploadReset();
          onChange({});
        }}
        onInputChange={event => {
          const [file] = event.target.files;
          onUpload(file);
        }}
        image={image}
        className="mb-2"
      />

      <hr />

      <h5 className="mb-0">Select CardTemplates for this CardSideGroup</h5>
      <p className="text-sm">These will be created as CardSides</p>

      {cardTemplates.map(cardTemplate => {
        if (cardTemplate.side !== cardSideGroup.side) {
          return null;
        }

        const isSelected = cardSideGroup.card_templates
          .map(({ id }) => id)
          .includes(cardTemplate.id);

        return (
          <Button
            key={cardTemplate.id}
            btnSize="sm"
            className="mr-1"
            onClick={() => onCardTemplateToggle(cardTemplate)}
            btnOutline={!isSelected}
            disabled={
              !isSelected &&
              cardSideGroup.card_templates.length >=
                cardSideGroup.allowed_card_templates
            }
          >
            {cardTemplate.name}
          </Button>
        );
      })}
      <hr />

      {selectedCardTemplates.map(cardTemplate => {
        return (
          <CardTemplateSettings
            key={cardTemplate.id}
            cardTemplate={cardTemplate}
            getElementConfigurationStateById={getElementConfigurationStateById}
            onElementConfigurationChange={onElementConfigurationChange}
            onCardTemplateChange={onCardTemplateChange}
          />
        );
      })}
    </BoxedItem>
  );
};

CardSideGroupSettings.propTypes = propTypes;

export default CardSideGroupSettings;
